import React, { useEffect, useRef, useState } from 'react';
import { translate } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { Input, Switch, Textarea } from '@fiverr-private/forms';
import { getContext } from '@fiverr-private/fiverr_context';
import { Stack } from '@fiverr-private/layout_components';
import { FormControl } from '@fiverr-private/orca';
import { Collection, CollectionFormFields } from '../../types';
import { LabelText } from '../LabelText/LabelText';

interface AddEditListFormProps {
    formRef: React.RefObject<HTMLFormElement>;
    onSubmit: (Collection: CollectionFormFields) => Promise<void>;
    collection?: Collection;
    showDescription?: boolean | 'auto';
}

const TITLE_MAX_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 330;

export const AddEditListForm = ({
    formRef,
    onSubmit,
    collection,
    showDescription: propsShowDescription,
}: AddEditListFormProps) => {
    const [errors, setErrors] = useState<Partial<Record<keyof Collection, string>>>();
    const nameInputRef = useRef<HTMLInputElement>(null);
    const {
        experience: { isBusiness },
    } = getContext();
    const isEditing = !!collection?.id;
    const showDescription = propsShowDescription === 'auto' ? isEditing : propsShowDescription;
    const defaultIsPrivate = isBusiness ? collection?.ownerType === 'USER' : true;

    useEffect(() => {
        if (nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, []);

    const validate = (values: CollectionFormFields) => {
        if (!values.name) {
            return { name: translate('collect_actions.add_edit_list_modal.fields.list_name_required') };
        }
    };

    const clearErrors = () => {
        if (errors) {
            setErrors(undefined);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const values: CollectionFormFields = {
            name: (formData.get('name') as string) || '',
            description: (formData.get('description') as string) || '',
            ownerType: formData.get('isPrivate') === 'on' || !isBusiness ? 'USER' : 'BUSINESS',
        };
        const errors = validate(values);

        if (errors) {
            setErrors(errors);
            return;
        }

        await onSubmit(values);
    };

    return (
        <form ref={formRef} onSubmit={handleSubmit}>
            <Stack direction="column" gap="4">
                <FormControl
                    id={`add-edit-list-name-${collection?.id || 'new'}`}
                    label={<LabelText>{translate('collect_actions.add_edit_list_modal.fields.list_name')}</LabelText>}
                    isError={!!errors?.name}
                    hint={errors?.name}
                >
                    <Input
                        ref={nameInputRef}
                        name="name"
                        maxLength={TITLE_MAX_LENGTH}
                        defaultValue={collection?.name}
                        placeholder={translate<string>(
                            'collect_actions.add_edit_list_modal.fields.list_name_placeholder'
                        )}
                        onChange={clearErrors}
                    />
                </FormControl>
                {showDescription && (
                    <FormControl
                        id={`add-edit-list-description-${collection?.id || 'new'}`}
                        isOptional
                        label={
                            <LabelText>{translate('collect_actions.add_edit_list_modal.fields.description')}</LabelText>
                        }
                    >
                        <Textarea
                            name="description"
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            defaultValue={collection?.description}
                            height={120}
                            placeholder={translate<string>(
                                'collect_actions.add_edit_list_modal.fields.description_placeholder'
                            )}
                        />
                    </FormControl>
                )}
                {isBusiness && (
                    <Switch name="isPrivate" defaultChecked={defaultIsPrivate} size="sm">
                        <Text color="bodyPrimary">
                            {translate('collect_actions.add_edit_list_modal.fields.is_private')}
                        </Text>
                    </Switch>
                )}
            </Stack>
        </form>
    );
};
